import _ from 'lodash';
import { Presence } from 'phoenix';
import { Delta } from 'quill/core';
import { ACTION_TANDEM_ERROR, CATEGORY_PRODUCT } from '../constants/analytics';
import * as Constants from '../constants/redux';
import replacePostPath from '../post/utils/replacePostPath';
import UIActions from '../ui/UIActions';
import { track } from '../utils/analytics';
import Logger from '../utils/Logger';
import SyncEngine from './SyncEngine';

const UNRECOVERABLE_ERRORS = [
  'checksum',
  'invalid_composition',
  'not_found',
  'server_behind',
  'unicode_split',
];

const debouncedTitleUpdate = _.debounce((dispatch, getState, navigate) => {
  const { editor } = getState();
  const post = { id: editor.postId, title: editor.title };
  replacePostPath(post, navigate);
}, 1000);

const EditorActions = {
  editingLink: (isEditing) => {
    return (dispatch) => {
      dispatch({ type: Constants.EDITOR_EDITING_LINK, editingLink: isEditing });
    };
  },

  pickingReaction: (isPicking) => {
    return (dispatch) => {
      dispatch({
        type: Constants.EDITOR_PICKING_REACTION,
        pickingReaction: isPicking,
      });
    };
  },

  editorMounted: (quill) => {
    return (dispatch, getState) => {
      const {
        editor: { initialContent, postId, readOnly },
      } = getState();
      if (initialContent.length() !== quill.getLength()) {
        if (!readOnly) {
          const errorToast = {
            level: 'error',
            message:
              'Post contents failed to load correctly. Please refresh the page to try again.',
          };
          dispatch(UIActions.pushToast(errorToast));
        }
        dispatch(EditorActions.lock());
        const associationKey = `${postId}: ${Math.random()
          .toString(36)
          .substring(2, 10)}`;

        const extra = {
          postContent: JSON.stringify(initialContent),
          editorContent: JSON.stringify(quill.getContents()),
          html: quill.root.innerHTML,
        };

        Object.keys(extra).forEach((key) => {
          Logger.error('Post load inconsistency', {
            associationKey,
            [key]: extra[key],
          });
        });
      }
    };
  },

  focusAnnotation: (id, isActive = true) => {
    return (dispatch) => {
      dispatch({
        type: Constants.EDITOR_FOCUS_ANNOTATION,
        id,
        isActive,
      });
    };
  },

  focusCommentThread: (id, isActive = true) => {
    return (dispatch) => {
      dispatch({
        type: Constants.EDITOR_FOCUS_COMMENT_THREAD,
        id,
        isActive,
      });
    };
  },

  focusReaction: (id, isActive = true) => {
    return (dispatch) => {
      dispatch({
        type: Constants.EDITOR_FOCUS_REACTION,
        id,
        isActive,
      });
    };
  },

  blurMagneticWall: () => {
    return (dispatch) => {
      dispatch({
        type: Constants.EDITOR_BLUR_MAGNETIC_WALL,
      });
    };
  },

  highlightAnnotation: (id) => {
    return (dispatch) => {
      dispatch({
        type: Constants.EDITOR_HIGHLIGHT_ANNOTATION,
        id,
      });
    };
  },

  highlightCommentThread: (id) => {
    return (dispatch) => {
      dispatch({
        type: Constants.EDITOR_HIGHLIGHT_COMMENT_THREAD,
        id,
      });
    };
  },

  highlightReaction: (id) => {
    return (dispatch) => {
      dispatch({
        type: Constants.EDITOR_HIGHLIGHT_REACTION,
        id,
      });
    };
  },

  removeMagneticWallHighlight: () => {
    return (dispatch) => {
      dispatch({
        type: Constants.EDITOR_REMOVE_MAGNETIC_WALL_HIGHLIGHT,
      });
    };
  },

  leave: () => {
    return (dispatch, getState) => {
      const {
        editor: { channel },
      } = getState();
      if (channel) {
        channel.leave();
      }
      dispatch({ type: Constants.EDITOR_LEAVE });
    };
  },

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  load: (post, editable = true, onRemoteDelta, navigate) => {
    return (dispatch, getState) => {
      const state = getState();
      const oldChannel = state.editor.channel;
      if (oldChannel) {
        oldChannel.leave();
      }
      const channel = state.session.socket.channel(`posts:${post.id}`);
      const initialContent = new Delta(post.content);
      const initialVersion = post.version;
      const sync = createSyncEngine(
        dispatch,
        getState,
        channel,
        initialVersion,
        initialContent,
        onRemoteDelta,
        navigate,
      );
      dispatch({
        type: Constants.EDITOR_LOAD,
        channel,
        sync,
        editable,
        postId: post.id,
        title: post.title,
        readOnly: post.publishedAt != null,
        content: initialContent,
      });
      initSyncEngine(dispatch, sync, channel);
      const presence = new Presence(channel);
      presence.onSync(() => {
        dispatch({
          type: Constants.EDITOR_PRESENCE,
          presence: presence.state,
        });
      });
      replacePostPath(post, navigate);
    };
  },

  transmitStateChange: (range) => {
    return (dispatch, getState) => {
      const {
        editor: { channel, readOnly, version, unsaved },
      } = getState();
      let pendingTransmitRange = readOnly ? null : range;
      if (pendingTransmitRange && channel && unsaved.length() === 0) {
        transmitSelection(channel, range, version);
        pendingTransmitRange = null;
      }
      dispatch({
        type: Constants.EDITOR_TRANSMIT_STATE_CHANGE,
        pendingTransmitRange,
      });
    };
  },

  localUpdated: (delta) => {
    return (dispatch, getState) => {
      const {
        editor: { sync },
      } = getState();
      sync.addLocalDelta(delta);
      dispatch({ type: Constants.EDITOR_LOCAL_UPDATED, delta });
    };
  },

  lock: () => {
    return (dispatch) => {
      dispatch({ type: Constants.EDITOR_LOCK });
    };
  },

  readOnly: (readOnly) => {
    return (dispatch, getState) => {
      const { editor } = getState();
      if (editor.locked && !readOnly) {
        const errorToast = {
          level: 'error',
          message:
            'Editor is locked due to an encountered error. Please refresh the page to retry.',
        };
        dispatch(UIActions.clearToasts());
        dispatch(UIActions.pushToast(errorToast));
      } else {
        const before = editor.readOnly;
        dispatch({ type: Constants.EDITOR_READ_ONLY, readOnly });
        const after = getState().editor.readOnly;
        if (before !== after) {
          dispatch(EditorActions.updateMeta());
        }
      }
      return Promise.resolve();
    };
  },

  remoteSelection: (mark, userId) => {
    return (dispatch) => {
      dispatch({ type: Constants.EDITOR_SELECTION_REMOTE, userId, mark });
    };
  },

  saveStatusChange: (saveStatus) => {
    return (dispatch) => {
      dispatch({ type: Constants.EDITOR_SAVE_STATUS_CHANGE, saveStatus });
    };
  },

  titleUpdate: (title, navigate) => {
    return (dispatch, getState) => {
      dispatch({ type: Constants.EDITOR_TITLE_UPDATE, title });
      debouncedTitleUpdate(dispatch, getState, navigate);
    };
  },

  updateMeta: () => {
    return (dispatch, getState) => {
      const {
        editor: { channel, readOnly },
      } = getState();
      if (channel) {
        channel.push('meta', { edit: !readOnly });
      }
      return Promise.resolve();
    };
  },

  syncStateChange: ({ version, unsaved }) => {
    return (dispatch, getState) => {
      dispatch({
        type: Constants.EDITOR_SYNC_STATE_CHANGE,
        version,
        unsaved,
      });
      const {
        editor: { pendingTransmitRange, channel },
      } = getState();
      if (pendingTransmitRange && unsaved.length() === 0) {
        transmitSelection(channel, pendingTransmitRange, version);
        dispatch({ type: Constants.EDITOR_SELECTION_TRANSMITTED });
      }
    };
  },

  setHasStateBar: (hasStateBar) => {
    return (dispatch) => {
      dispatch({ type: Constants.EDITOR_SET_HAS_STATE_BAR, hasStateBar });
    };
  },

  setIsPostBannerPopOverOpen: (isPostBannerPopOverOpen) => {
    return (dispatch) => {
      dispatch({
        type: Constants.EDITOR_SET_POST_BANNER_POPOVER_OPEN,
        isPostBannerPopOverOpen,
      });
    };
  },

  setIsPositioningPostBanner: (isPositioningPostBanner) => {
    return (dispatch) => {
      dispatch({
        type: Constants.EDITOR_SET_IS_POSITIONING_POST_BANNER,
        isPositioningPostBanner,
      });
    };
  },

  setCurrentPostBanner: (currentPostBanner) => {
    return (dispatch) => {
      dispatch({
        type: Constants.EDITOR_SET_CURRENT_POST_BANNER,
        currentPostBanner,
      });
    };
  },
};

export default EditorActions;

function createSyncEngine(
  dispatch,
  getState,
  channel,
  initialVersion,
  initialContent,
  onRemoteDelta,
  navigate,
) {
  const sync = new SyncEngine({
    initialVersion,
    initialChecksum: initialContent.length(),
    sendCommit({ baseVersion, baseChecksum, delta, clientId }) {
      setTimeout(() => {
        channel
          .push('update', {
            version: baseVersion,
            delta,
            checksum: baseChecksum,
            clientId,
          })
          .receive('error', (resp) => {
            if (resp && UNRECOVERABLE_ERRORS.includes(resp.error)) {
              dispatch(EditorActions.lock());
              track(ACTION_TANDEM_ERROR, {
                category: CATEGORY_PRODUCT,
                label: `update_${resp.error}`,
              });
              sync.setIsConnected(false);
              channel.leave();
            }
          });
      }, 200); // Increase to test realtime collaboration
    },
    sendSync({ baseVersion }) {
      return new Promise((accept, reject) => {
        channel
          .push('sync', { version: baseVersion })
          .receive('ok', ({ revisions }) => {
            accept({
              revisions: revisions.map((rev) => ({
                version: rev.version,
                delta: new Delta(rev.delta),
                clientId: rev.clientId,
                checksum: rev.checksum,
              })),
            });
          })
          .receive('timeout', reject)
          .receive('error', (resp) => {
            if (resp && UNRECOVERABLE_ERRORS.includes(resp.error)) {
              const { editor } = getState();
              if (!editor.readOnly) {
                const errorToast = {
                  level: 'error',
                  message:
                    'Could not connect for editing. Please refresh the page to reconnect.',
                };
                dispatch(UIActions.pushToast(errorToast));
              }
              dispatch(EditorActions.lock());
              track(ACTION_TANDEM_ERROR, {
                category: CATEGORY_PRODUCT,
                label: `sync_${resp.error}`,
              });
              sync.setIsConnected(false);
              channel.leave();
            } else {
              reject();
            }
          });
      });
    },
    onRemoteDelta(delta) {
      onRemoteDelta(delta);
      dispatch({ type: Constants.EDITOR_REMOTE_UPDATED, delta });

      const { editor } = getState();
      if (editor.quill) {
        const title = editor.quill.getTitle();
        if (title !== editor.title) {
          dispatch(EditorActions.titleUpdate(title, navigate));
        }
      }
    },
    onStateChange(syncState) {
      dispatch(EditorActions.syncStateChange(syncState));
    },
    onSyncStatusChange(syncStatus) {
      dispatch({ type: Constants.EDITOR_SYNC_STATUS_CHANGE, syncStatus });
    },
  });
  channel.on('selection', ({ range, userId }) => {
    dispatch(EditorActions.remoteSelection(range, userId));
  });
  channel.on('update', (payload) => {
    const { version, delta, clientId, checksum } = payload;
    sync.receiveUpdate({
      version,
      delta: new Delta(delta),
      clientId,
      checksum,
    });
  });
  channel.onClose(() => {
    sync.setIsConnected(false);
  });
  channel.onError(() => {
    // Occurs (but not limted to) on network loss
    sync.setIsConnected(false);
  });
  return sync;
}

function initSyncEngine(dispatch, sync, channel) {
  dispatch({
    type: Constants.EDITOR_SYNC_STATE_CHANGE,
    ...sync.getSyncState(),
  });
  channel.join().receive('ok', () => {
    sync.setIsConnected(true);
    dispatch(EditorActions.updateMeta());
  });
}

function transmitSelection(channel, range, version) {
  channel.push('selection', { range, version });
}
